import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import RiskWarning from "../components/RiskWarning";
import { Shield, TrendingUp, Users, Award } from "lucide-react";

export default function Home() {
  return (
    <div id="main-content" className="pt-24" data-testid="home-page">
      <section className="min-h-screen flex items-center justify-center px-6 lg:px-12" data-testid="hero-section">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-8 inline-block">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#d4af69] to-transparent mx-auto mb-8"></div>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-8xl font-light mb-8 text-[#e8dcc4] leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Wealth Management
            <span className="block text-[#d4af69] mt-4 text-4xl sm:text-5xl lg:text-7xl">Refined for Excellence</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-400 mb-16 max-w-3xl mx-auto font-light leading-relaxed">
            Experience sophisticated wealth management where every investment decision is guided by deep expertise,
            unwavering integrity, and a relentless commitment to your financial aspirations.
            We transform complexity into clarity, delivering exceptional outcomes that transcend expectations.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <Link to="/contact">
              <Button size="lg" className="bg-[#d4af69] hover:bg-[#c29d5a] text-[#0d1117] font-medium px-12 py-7 text-base tracking-wider uppercase shadow-lg shadow-[#d4af69]/20 hover:shadow-[#d4af69]/40 transition-all" data-testid="get-started-btn">
                Get Started
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-2 border-[#d4af69]/30 text-[#d4af69] hover:bg-[#d4af69]/10 hover:border-[#d4af69] font-medium px-12 py-7 text-base tracking-wider uppercase transition-all" data-testid="learn-more-btn">
                Learn More
              </Button>
            </Link>
          </div>
          <div className="max-w-4xl mx-auto">
            <RiskWarning />
          </div>
        </div>
      </section>

      <section className="py-32 px-6 lg:px-12" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#d4af69] to-transparent mx-auto mb-8"></div>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#e8dcc4] mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Why Choose Kredere
            </h2>
            <p className="text-gray-400 font-light max-w-2xl mx-auto">Excellence in distribution, grounded in regulatory compliance and client trust.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="luxury-card p-8 rounded-lg group" data-testid="feature-card-compliance">
              <div className="w-16 h-16 rounded-full bg-[#d4af69]/10 flex items-center justify-center mb-6 group-hover:bg-[#d4af69]/20 transition-colors">
                <Shield className="w-8 h-8 text-[#d4af69]" />
              </div>
              <h3 className="text-2xl font-light mb-4 text-[#e8dcc4]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Regulatory Compliance</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">
                AMFI-registered distributor for mutual funds and SIF, ensuring full regulatory adherence.
              </p>
            </div>
            <div className="luxury-card p-8 rounded-lg group" data-testid="feature-card-expertise">
              <div className="w-16 h-16 rounded-full bg-[#d4af69]/10 flex items-center justify-center mb-6 group-hover:bg-[#d4af69]/20 transition-colors">
                <Award className="w-8 h-8 text-[#d4af69]" />
              </div>
              <h3 className="text-2xl font-light mb-4 text-[#e8dcc4]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Expert Distribution</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">
                Specialized in distributing diverse mutual fund and investment products tailored to client needs.
              </p>
            </div>
            <div className="luxury-card p-8 rounded-lg group" data-testid="feature-card-transparent">
              <div className="w-16 h-16 rounded-full bg-[#d4af69]/10 flex items-center justify-center mb-6 group-hover:bg-[#d4af69]/20 transition-colors">
                <TrendingUp className="w-8 h-8 text-[#d4af69]" />
              </div>
              <h3 className="text-2xl font-light mb-4 text-[#e8dcc4]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Transparent Process</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">
                Clear product information and transparent dealings with no misleading claims.
              </p>
            </div>
            <div className="luxury-card p-8 rounded-lg group" data-testid="feature-card-client">
              <div className="w-16 h-16 rounded-full bg-[#d4af69]/10 flex items-center justify-center mb-6 group-hover:bg-[#d4af69]/20 transition-colors">
                <Users className="w-8 h-8 text-[#d4af69]" />
              </div>
              <h3 className="text-2xl font-light mb-4 text-[#e8dcc4]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Client-Centric</h3>
              <p className="text-gray-400 text-sm font-light leading-relaxed">
                Dedicated support with structured grievance redressal and investor protection mechanisms.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 px-6 lg:px-12" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="h-px w-24 bg-gradient-to-r from-transparent via-[#d4af69] to-transparent mx-auto mb-8"></div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-8 text-[#e8dcc4]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Ready to Begin?
          </h2>
          <p className="text-lg text-gray-400 mb-12 font-light">
            Connect with us to explore mutual fund and SIF distribution opportunities.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#d4af69] hover:bg-[#c29d5a] text-[#0d1117] font-medium px-12 py-7 text-base tracking-wider uppercase shadow-lg shadow-[#d4af69]/20 hover:shadow-[#d4af69]/40 transition-all" data-testid="contact-cta-btn">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}